import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import SiteProofLogo from '../../components/SiteProofLogo';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useLanguage } from '../../contexts/LanguageContext';

const ResetPassword = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [status, setStatus] = useState('loading'); // loading | ready | submitting | success | invalid
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    let cancelled = false;

    // PKCE recovery link arrives as `/reset-password?code=xxx`. We exchange
    // the code for a session explicitly so we can distinguish a real recovery
    // from a stale link. detectSessionInUrl may also auto-exchange the same
    // code on client init — if that race wins, our exchange returns an error
    // and we fall back to getSession() to see if a session already exists.
    //
    // The legacy implicit-flow path (#access_token=...&type=recovery) is kept
    // as a fallback via the onAuthStateChange listener below: if for any
    // reason an implicit-style link still arrives (e.g. a pre-cutover email
    // a user clicks after we ship), supabase-js will parse the hash and fire
    // PASSWORD_RECOVERY, which we still honour.

    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) {
        setStatus(prev => (prev === 'ready' ? prev : 'ready'));
      }
    });

    (async () => {
      try {
        const params = new URLSearchParams(window.location?.search || '');
        const code = params?.get('code');
        const errorParam = params?.get('error') || params?.get('error_code');
        const errorDescription = params?.get('error_description');

        // Supabase appends ?error=... when the upstream verify step rejects
        // the token (expired, already used, etc.). Surface that directly
        // instead of waiting for the timeout.
        if (errorParam) {
          if (!cancelled) {
            setErrorMsg(
              errorDescription
                ? decodeURIComponent(errorDescription.replace(/\+/g, ' '))
                : 'This password reset link is invalid or has expired. Please request a new one.'
            );
            setStatus('invalid');
          }
          return;
        }

        if (code) {
          // Strip ?code= from the URL so a refresh doesn't try to re-exchange
          // a now-consumed code (which would surface as "invalid link").
          window.history?.replaceState({}, document.title, window.location?.pathname);

          const { data, error } = await supabase?.auth?.exchangeCodeForSession(code);

          if (cancelled) return;

          if (error || !data?.session) {
            // Race: detectSessionInUrl may have already exchanged this code
            // on client init. Check for an existing session before declaring
            // the link dead.
            const { data: existing } = await supabase?.auth?.getSession();
            if (existing?.session?.user) {
              setStatus('ready');
              return;
            }
            setErrorMsg('This password reset link is invalid or has expired. Please request a new one.');
            setStatus('invalid');
            return;
          }

          setStatus('ready');
          return;
        }

        // No ?code= and no ?error=: either a legacy implicit-flow hash link
        // (handled by the onAuthStateChange listener above) or a user landed
        // here directly without a token. Give the auto-detect a brief window
        // to fire PASSWORD_RECOVERY before declaring the link invalid.
      } catch (_) {
        if (!cancelled) {
          setErrorMsg('This password reset link is invalid or has expired. Please request a new one.');
          setStatus('invalid');
        }
      }
    })();

    // Safety-net timeout for the implicit/no-token path. If we already
    // transitioned out of 'loading' (via code exchange or PASSWORD_RECOVERY),
    // the setter is a no-op.
    const timeout = setTimeout(() => {
      if (cancelled) return;
      setStatus(prev => {
        if (prev === 'loading') {
          setErrorMsg('This password reset link is invalid or has expired. Please request a new one.');
          return 'invalid';
        }
        return prev;
      });
    }, 5000);

    return () => {
      cancelled = true;
      subscription?.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const validate = () => {
    const newErrors = {};
    if (!password) {
      newErrors.password = t('reset.pw_required');
    } else if (password?.length < 8) {
      newErrors.password = t('reset.pw_min');
    }
    if (!confirmPassword) {
      newErrors.confirmPassword = t('reset.confirm_required');
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = t('reset.confirm_mismatch');
    }
    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!validate()) return;

    setStatus('submitting');
    setErrorMsg('');

    // Add this line - use supabase.auth.updateUser instead of authService
    const { error } = await supabase?.auth?.updateUser({ password });

    if (error) {
      const msg = error?.message || '';
      if (
        msg?.toLowerCase()?.includes('expired') ||
        msg?.toLowerCase()?.includes('invalid') ||
        msg?.toLowerCase()?.includes('token')
      ) {
        setErrorMsg('This reset link has expired or is no longer valid. Please request a new password reset.');
        setStatus('invalid');
      } else {
        setErrorMsg(msg || 'Failed to update password. Please try again.');
        setStatus('ready');
      }
    } else {
      setStatus('success');
      // Sign out so user logs in fresh with new password
      await supabase?.auth?.signOut();
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4 py-8 md:py-12">
      <div className="w-full max-w-md">
        {/* Logo / Brand */}
        <div className="flex justify-center mb-8">
          <SiteProofLogo size="lg" iconOnly />
        </div>

        <div className="bg-card rounded-lg shadow-elevation-3 border border-border p-6 md:p-8">
          {/* Loading */}
          {status === 'loading' && (
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent"></div>
              </div>
              <p className="text-sm text-muted-foreground">{t('reset.verifying')}</p>
            </div>
          )}

          {/* Invalid / Expired */}
          {status === 'invalid' && (
            <div className="space-y-5">
              <div className="flex items-start gap-3 p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
                <Icon name="AlertCircle" size={20} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-destructive mb-1">{t('reset.invalid_title')}</p>
                  <p className="text-sm text-destructive/80 leading-relaxed">{errorMsg}</p>
                </div>
              </div>
              <Button
                variant="default"
                fullWidth
                onClick={() => navigate('/login')}
              >
                {t('reset.back_to_signin')}
              </Button>
            </div>
          )}

          {/* Ready to set new password */}
          {(status === 'ready' || status === 'submitting') && (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <h2 className="text-xl font-semibold text-foreground mb-1 font-heading">{t('reset.set_new_password')}</h2>
                <p className="text-sm text-muted-foreground">{t('reset.set_new_password_desc')}</p>
              </div>

              {errorMsg && (
                <div className="flex items-start gap-3 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <Icon name="AlertCircle" size={18} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-destructive">{errorMsg}</p>
                </div>
              )}

              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  label={t('reset.new_password_label')}
                  placeholder={t('reset.new_password_placeholder')}
                  value={password}
                  onChange={(e) => {
                    setPassword(e?.target?.value);
                    if (errors?.password) setErrors(prev => ({ ...prev, password: '' }));
                  }}
                  error={errors?.password}
                  required
                  disabled={status === 'submitting'}
                  className="w-full"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-smooth"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
                </button>
              </div>

              <div className="relative">
                <Input
                  type={showConfirmPassword ? 'text' : 'password'}
                  label={t('reset.confirm_password_label')}
                  placeholder={t('reset.confirm_password_placeholder')}
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e?.target?.value);
                    if (errors?.confirmPassword) setErrors(prev => ({ ...prev, confirmPassword: '' }));
                  }}
                  error={errors?.confirmPassword}
                  required
                  disabled={status === 'submitting'}
                  className="w-full"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-[38px] text-muted-foreground hover:text-foreground transition-smooth"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={20} />
                </button>
              </div>

              <Button
                type="submit"
                variant="default"
                fullWidth
                loading={status === 'submitting'}
              >
                {t('reset.set_btn')}
              </Button>
            </form>
          )}

          {/* Success */}
          {status === 'success' && (
            <div className="space-y-5">
              <div className="flex items-start gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                <Icon name="CheckCircle2" size={20} color="#16a34a" className="flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-green-900 mb-1">{t('reset.success_title')}</p>
                  <p className="text-sm text-green-800 leading-relaxed">
                    {t('reset.success_desc')}
                  </p>
                </div>
              </div>
              <Button
                variant="default"
                fullWidth
                onClick={() => navigate('/login')}
              >
                {t('reset.sign_in_new')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
